import dayjs from 'dayjs';
import type { BasicEntry } from '../services/types/vacancy.types';
import type { Option } from '../components/common/AsyncAutocomplete';
import type {
  CustomerEntry,
  QualificationEntry,
  SkillEntry,
  TechnologyEntry,
} from '../services/types/vacancy.types';

export function validateVacancyForm(
  basicData: BasicEntry,
  customers: CustomerEntry[],
  qualifications: QualificationEntry[],
  primarySkills: SkillEntry[],
  primaryTechnologies: TechnologyEntry[],
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  if (!basicData.vacancyType) {
    newErrors.vacancyType = 'Vacancy type is required';
  }
  if (!basicData.vacancyTitle) {
    newErrors.vacancyTitle = 'Vacancy title is required';
  }
  if (!basicData.vacancyCode) {
    newErrors.vacancyCode = 'Vacancy code is required';
  }
  if (!basicData.minIndustryExperience) {
    newErrors.minIndustryExperience = 'Minimum industry experience is required';
  }
  if (!basicData.startDate) {
    newErrors.startDate = 'Start date is required';
  }
  if (!basicData.endDate) {
    newErrors.endDate = 'End date is required';
  }
  if (!basicData.dueDate) {
    newErrors.dueDate = 'Deadline is required';
  }
  if (!basicData.vacancyReason) {
    newErrors.vacancyReason = 'Vacancy reason is required';
  }

  customers.forEach((customer, index) => {
    if (!customer.id) {
      newErrors[`customer_${index}_id`] = 'Customer is required';
    }

    if (!customer.numberOfVacancies) {
      newErrors[`customer_${index}_numberOfVacancies`] = 'Number of vacancies is required';
    } else if (
      isNaN(Number(customer.numberOfVacancies)) ||
      Number(customer.numberOfVacancies) <= 0
    ) {
      newErrors[`customer_${index}_numberOfVacancies`] =
        'Number of vacancies must be greater than zero';
    }

    if (!customer.revenuePerResource) {
      newErrors[`customer_${index}_revenuePerResource`] = 'Revenue per resource is required';
    } else if (
      isNaN(Number(customer.revenuePerResource)) ||
      Number(customer.revenuePerResource) <= 0
    ) {
      newErrors[`customer_${index}_revenuePerResource`] =
        'Revenue per resource must be greater than zero';
    }
  });

  const hasValidQualification = qualifications.some(
    (qualification) => qualification.qualification && qualification.type,
  );
  if (!hasValidQualification) {
    newErrors.qualifications = 'At least one qualification is required';
  }

  const hasValidPrimarySkill = primarySkills.some((skill) => skill.skill && skill.years);
  if (!hasValidPrimarySkill) {
    newErrors.primarySkills = 'At least one primary skill is required';
  }

  const hasValidPrimaryTechnology = primaryTechnologies.some(
    (technology) => technology.technology && technology.years,
  );
  if (!hasValidPrimaryTechnology) {
    newErrors.primaryTechnologies = 'At least one primary technology is required';
  }

  if (
    basicData.startDate &&
    basicData.endDate &&
    dayjs(basicData.startDate).isAfter(dayjs(basicData.endDate))
  ) {
    newErrors.endDate = 'End date must be after start date';
  }

  return newErrors;
}

export interface JDLocationRow {
  city: Option | null;
  country: Option | null;
  budgetAmount: string;
  budgetCurrency: string;
}

/** Returns field errors for JD tab (locations, aboutEyepax, jobDescription). */
export function validateJDForm(
  locations: JDLocationRow[],
  aboutEyepax: string,
  jobDescription: string,
): Record<string, string> {
  const newErrors: Record<string, string> = {};

  const hasValidLocation = locations.some(
    (location) =>
      location.country && location.city && location.budgetAmount?.trim(),
  );
  if (!hasValidLocation) {
    newErrors.locations = 'At least one location with country, city, and budget is required';
  }

  if (!aboutEyepax || !aboutEyepax.trim()) {
    newErrors.aboutEyepax = 'About Eyepax is required';
  }

  if (!jobDescription || !jobDescription.trim()) {
    newErrors.jobDescription = 'Job Description is required';
  }

  return newErrors;
}

export interface QuestionRowComparable {
  id: number | null;
  interviewQuestionId: number | null;
  question: string;
  answerType: string;
  /** Legacy interview level; vacancy stages replaced it, so it is optional and not compared. */
  questionLevel?: 0 | 1 | 5;
  applicantResponse: number;
  sortOrder: number;
  isActive: boolean;
  _source: 'normal' | 'predefined';
}

export function validateQuestionRowsForLevel(
  questions: Array<{
    _clientId: string;
    question: string;
    answerType: string;
    _source: 'normal' | 'predefined';
  }>,
): {
  fieldErrors: Record<string, { question?: string; answerType?: string }>;
  hasErrors: boolean;
} {
  const newFieldErrors: Record<string, { question?: string; answerType?: string }> = {};
  let hasErrors = false;

  questions.forEach((q) => {
    const rowErrors: { question?: string; answerType?: string } = {};

    if (!q.question || !q.question.trim()) {
      rowErrors.question = 'Question is required';
      hasErrors = true;
    }

    if (q._source === 'normal' && !q.answerType) {
      rowErrors.answerType = 'Answer type is required';
      hasErrors = true;
    }

    if (Object.keys(rowErrors).length > 0) {
      newFieldErrors[q._clientId] = rowErrors;
    }
  });

  return { fieldErrors: newFieldErrors, hasErrors };
}

export function levelQuestionsEqual<T extends QuestionRowComparable>(
  draft: T[],
  saved: T[],
): boolean {
  if (draft.length !== saved.length) return false;
  return draft.every((draftQ, index) => {
    const savedQ = saved[index];
    if (!savedQ) return false;
    return (
      draftQ.id === savedQ.id &&
      draftQ.interviewQuestionId === savedQ.interviewQuestionId &&
      (draftQ.question ?? '').trim() === (savedQ.question ?? '').trim() &&
      draftQ.answerType === savedQ.answerType &&
      draftQ.sortOrder === savedQ.sortOrder &&
      draftQ.isActive === savedQ.isActive
    );
  });
}
